import { GameAudio, getMatchSound } from '../../Config/Audio';
import { BOOSTER_LIST, ItemIDs } from '../../Config/Items';
import { getMatchScore } from '../../Config/Tiles';
import Core from '../../Core/Core';
import getLogger, { ILogger } from '../../Core/Logger';
import { IBoardMap } from '../../Game/BoardArrangement';
import BoardUtils from '../../Game/BoardUtils';
import SoundManager from '../../Sound/SoundManager';
import { Actions, GameContainerAction } from '../../State/Actions/Actions';
import { IStateBoard } from '../../State/reducers/board';
import { IStateLevel, StuckReasons } from '../../State/reducers/level';
import { IGameState } from '../../State/Store/store';
import { MagnetBlacklist, Suits } from '../../Tiles/TileSet';
import { BoardSprite } from '../../UI/Game/Board/BoardSprite';
import { BoardUI } from '../../UI/Game/Board/BoardUI';
import { TileSprite } from '../../UI/Game/Board/TileSprite';
import { waitForMs } from '../../Utils/AsyncUtils';
import { PauseListener } from '../../Utils/Pausable';
import { disableBoardAnimations } from '../../Utils/Platform';
import { equalArrays } from '../../Utils/Utils';
import { FocusManager } from '../FocusManager';
import { HudActions, HudEmitter } from '../Hud/HudEmitter';
import { Manager } from '../Manager';
import FTUETracking, { FTUE_TRACK_STAGES } from '../Tracking/FTUETracking';

const boardLogger: ILogger = getLogger('[BOARD]:');

export default class Board extends Manager {
    public static enableBoosters(boosterId: string, enabled: boolean) {
        if (
            !this.comp ||
            !this.comp.hud ||
            !this.comp.hud.ui ||
            !this.comp.hud.ui.bottomUi ||
            !BOOSTER_LIST.includes(boosterId as ItemIDs)
        ) {
            return;
        }
        this.comp.hud.ui.bottomUi.boosters.getButton(boosterId).enabled = enabled;
    }

    private static comp: BoardUI;
    public view: BoardUI;
    private focusListener: PauseListener;
    private settingsListener: PauseListener;
    private tileClickListener: (id: string) => void;
    private boardReady: boolean = false;
    private settingsVisible: boolean;
    private matchSuit: Suits;

    constructor(view: BoardUI) {
        super();

        this.view = view;
        this.tileClickListener = this.handleTileClick.bind(this);
        this.view.on(GameContainerAction.TILE_SELECTED, this.tileClickListener);
        this.focusListener = this.showTileType.bind(this);
        this.settingsListener = (visible: boolean) => {
            this.settingsVisible = visible;
            this.showTileType(!visible);
        };
        FocusManager.instance.addListener(this.focusListener);
        HudEmitter.instance.on(HudActions.SET_PAUSE_VISIBLE, this.settingsListener);
        Board.comp = this.view;

        const boardChildren = this.view.boardLayer.children.slice(0);
        boardChildren.map(c => {
            this.view.scatterLayer.addChild(c);
        });
        this.view.createTileSprites(Core.store.getState().StateBoard.boardTiles);

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.showSelected(state, pre);
        });

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.showAvailable(state, pre);
        });

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.handleBoosterChange(state, pre);
        });

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.handleStateChange(state, pre);
        });

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.showBoardMatch(state, pre);
        });

        this.addSelector(
            (state: IStateLevel, pre: IStateLevel) => {
                this.handleSpritesChange(state, pre);
            },
            (state: IGameState) => state.StateLevel
        );
    }

    public async initBoard() {
        const state = Core.store.getState();
        const pre = Core.store.getPreviousState();
        const {
            StateBoard: { boardTiles },
        } = state;

        if (!disableBoardAnimations()) {
            await this.view.animateTiles(boardTiles);
        }

        this.handleStateChange(state, pre);

        this.boardReady = true;

        return;
    }

    public handleStateChange(state: IGameState, previous: IGameState) {
        const board: IStateBoard = state.StateBoard;
        const level: IStateLevel = state.StateLevel;

        const tiles = board.boardTiles.filter(t => {
            if (level.matches.find(m => t.locId === m) || level.found.find(f => t.locId === f)) {
                return false;
            }
            return t;
        });

        if (!this.view) {
            return;
        }
        const { found } = level;
        const { found: pFound } = previous.StateLevel;
        if (found > pFound) {
            this.collectFound(found, board.boardMap);
        }

        if (!state.StatePause.paused && !state.StatePause.inputPaused && this.boardReady) {
            const boardSizeChanged = board.boardSize !== this.view.boardSize;
            if (boardSizeChanged) {
                this.view.boardSize = board.boardSize;
            }
            this.view.displayTiles(tiles);
        }
    }

    public shuffle() {
        Core.store.dispatch(Actions.shuffleTiles());
    }

    public update() {
        // update
    }

    public cleanUp() {
        super.cleanUp();

        HudEmitter.instance.off(HudActions.SET_PAUSE_VISIBLE, this.settingsListener);
        FocusManager.instance.removeListener(this.focusListener);
        this.view.off(GameContainerAction.TILE_SELECTED, this.tileClickListener);
        this.view.destroy();
        this.view = null;
    }

    public retry() {
        SoundManager.playMusic('game-music', true, 1000);
        this.view.cleanUpForRetry();
    }

    public showAvailable(state: IGameState, previous: IGameState) {
        const {
            StateBoard: { available: a1, boardMap },
        } = state;
        const {
            StateBoard: { available: a2 },
        } = previous;
        const {
            StateBoosters: { currentBoosters: previousBoosters },
            StatePause: { paused: previousPaused },
        } = previous;
        const {
            StateBoosters: { currentBoosters: boosters },
            StatePause: { paused },
        } = state;

        if (
            !equalArrays([a1, a2]) ||
            boosters.length !== previousBoosters.length ||
            paused !== previousPaused
        ) {
            const available = state.StateBoard.available;
            const show =
                boosters.includes(ItemIDs.HIGHLIGHT) ||
                (boosters.includes(ItemIDs.MAGNET) && state.StateLevel.stuck);
            Object.values(boardMap)
                .filter(t => t.tileType.suit !== 'special')
                .forEach(t => {
                    this.view.spriteMap[t.boardId].setAvailable(
                        available.includes(t.boardId),
                        show
                    );
                });
        }
    }

    public showBurst(score: number, m: number, gold?: boolean) {
        waitForMs(TileSprite.TILE_MATCH_TIME * 0.85).then(() => {
            const soundId = gold ? GameAudio.MATCH_GOLD : getMatchSound(m);
            this.view.showTileBurst(score * m, soundId, null, gold);
        });
    }

    public magnetShow(magnetId: string) {
        this.view.magnet(magnetId);
    }

    public magnetPull(tileId: string, magnetId: string, score: number) {
        this.view.pullToMagnet(tileId, magnetId, score);
    }

    public showBoardHint(tileIds: string[], ms: number) {
        this.view.revealMatch(tileIds, ms);
    }

    public blowUpTile(tileId: string, score?: number) {
        this.view.firecracker(tileId, score);
        this.view.spriteMap[tileId].blowUp();
    }

    private handleTileClick(id: string) {
        const currentState = Core.store.getState();
        const currentBoardState = currentState.StateBoard;
        const gameAvailable =
            currentBoardState.ready &&
            !currentBoardState.blocked &&
            !currentState.StatePause.paused &&
            !currentState.StatePause.inputPaused;

        if (gameAvailable) {
            if (currentState.StateScore.winTime === -1) {
                Core.store.dispatch(Actions.setWinTime(currentState.StateLevel.winTime));
            }
            const { StateBoard, StateBoosters } = Core.store.getState();
            const tile = StateBoard.boardMap[id];
            if (!tile) {
                return;
            }
            const { suit } = tile.tileType;
            const isBlackListed =
                StateBoosters.waitingBoosters.includes(ItemIDs.MAGNET) &&
                MagnetBlacklist.includes(suit as Suits);
            const tileAvailable = currentBoardState.available.includes(id) && !isBlackListed;

            if (tileAvailable) {
                Core.store.dispatch(Actions.selectTile(id));
            } else {
                Board.comp.spriteMap[id].playBlocked();
            }
        }
    }

    private showTileType(show: boolean): void {
        this.view.showTileType(show && !this.settingsVisible);
    }

    private showSelected(state: IGameState, previous: IGameState) {
        const { StateLevel: level } = state;
        const { StateLevel: prev } = previous;

        if (!equalArrays([level.selected, prev.selected])) {
            FTUETracking.track(FTUE_TRACK_STAGES.TILES_SELECTED, state);
            // selected: string[], m: IBoardMap
            const {
                StateBoard: { boardMap },
            } = state;
            Object.values(boardMap)
                .filter(t => t.tileType.suit !== 'special')
                .forEach(t => {
                    this.view.spriteMap[t.boardId].setSelected(level.selected.includes(t.boardId));
                });
        }
    }

    private showBoardMatch(state: IGameState, previous: IGameState) {
        const {
            StateLevel: { matches: m },
        } = state;
        const {
            StateLevel: { matches: p },
        } = previous;

        if (m.length > 0 && p.length === 0) {
            const {
                StateBoard: { boardMap, boardTiles },
            } = state;
            const {
                StateLevel: { matches },
            } = state;
            this.matchSuit = matches.map(t => BoardUtils.convertTileData(t))[0].suit as Suits;
            this.view.displayTiles(boardTiles);
            this.view.showTileMatch(matches, boardMap);
        }

        if (m.length === 0 && p.length !== 0) {
            const matchScore = getMatchScore(this.matchSuit);
            this.showBurst(matchScore, state.StateScore.multiplier, this.matchSuit === Suits.GOLD);
            this.matchSuit = null;
        }
    }

    private handleBoosterChange(state: IGameState, previous: IGameState) {
        const { StateBoosters: boosters } = state;
        const { StateBoosters: past } = previous;
        const added = boosters.currentBoosters.length > past.currentBoosters.length;

        if (!equalArrays([boosters.currentBoosters, past.currentBoosters])) {
            if (added) {
                if (
                    past.currentBoosters.includes(ItemIDs.HINT) &&
                    boosters.currentBoosters.includes(ItemIDs.HINT)
                ) {
                    this.view.stopHints();
                }
            } else {
                if (
                    !boosters.currentBoosters.includes(ItemIDs.HIGHLIGHT) &&
                    past.currentBoosters.includes(ItemIDs.HIGHLIGHT)
                ) {
                    this.view.clearHighlight();
                }
            }
        }
    }

    private handleSpritesChange(state: IStateLevel, previous: IStateLevel) {
        if (
            (state.shuffling && !previous.shuffling) ||
            (state.levelIndex !== previous.levelIndex && state.levelIndex !== 0)
        ) {
            if (state.shuffling) {
                this.scatterShuffle(Core.store.getState().StateBoard.boardMap);
            }
            this.compSetSprites();
        }
    }

    private compSetSprites() {
        const {
            StateBoard: { boardTiles },
        } = Core.store.getState();
        const boardChildren = this.view.boardLayer.children.slice(0);
        boardChildren.forEach(c => {
            this.view.boardLayer.addChild(c);
        });

        this.view.createTileSprites(boardTiles);
        Core.store.dispatch(Actions.setTilesprites());
    }

    private collectFound(found: string[], m: IBoardMap) {
        const collect = this.view.collectBoardObjects(found, m);
        collect.catch(err => {
            boardLogger.debug('error with collect board objects: ' + err);
        });
    }

    private scatterShuffle(m: IBoardMap) {
        const boardChildren = this.view.boardLayer.children.slice(0);
        boardChildren.map(c => {
            this.view.scatterLayer.addChild(c);
        });

        this.view.scatterShuffled(this.view.scatterLayer.children as BoardSprite[], m).then(() => {
            Core.store.dispatch(Actions.removeBoosters([ItemIDs.SHUFFLE]));
            Core.store.dispatch(
                Actions.setLevelStuck({ stuck: false, reason: StuckReasons.MATCHES })
            );
        });
    }
}
